import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMobileScreenButton } from "@fortawesome/free-solid-svg-icons";
import { usePwaInstall } from "../../../hooks/usePwaInstall";
import { useWebPush } from "../../../hooks/useWebPush";
import IosInstallSteps from "../../../components/pwa/IosInstallSteps";
import {
  PushDeviceReport,
  useReportPushDeviceMutation,
} from "../../../services/rtkApi/messengerReachApi";
import type { GameReach } from "../api/types";
import { pushCounts, pushNeedsApp } from "./reachRules";

// same key PushResyncListener uses for this browser's install id
const INSTALL_ID_KEY = "n450s:install-id";

export function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    typeof Notification !== "undefined"
  );
}

export function localPermission(): NotificationPermission | "unsupported" {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

function isMacSafari(isIOS: boolean, isSafari: boolean): boolean {
  return !isIOS && isSafari && typeof navigator !== "undefined" && /Macintosh/.test(navigator.userAgent);
}

interface Props {
  reach: GameReach | null;
  /** Something changed on this device (push on, app installed): check again now. */
  onChanged: () => void;
}

/**
 * Option A in the reach step: the site as an app with notifications on.
 * Per device: iPhone needs Safari's Add to Home Screen first (push only exists
 * in the installed app there); Android and desktop Chromium get the install
 * prompt; any browser with web push can turn notifications on in the tab.
 */
const PushOption: React.FC<Props> = ({ reach, onChanged }) => {
  const pwa = usePwaInstall();
  const push = useWebPush(true);
  const [reportDevice] = useReportPushDeviceMutation();
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // what the server counts (`via`), not just "push is on somewhere"
  const pushOn = pushCounts(reach);
  const needsApp = pushNeedsApp(reach) && !pwa.isStandalone;
  const supported = pushSupported();
  const permission = localPermission();
  const onHere = push.localSubscribed === true && permission === "granted";

  const turnOn = async () => {
    setBusy(true);
    setLocalError(null);
    try {
      const ok = await push.subscribe();
      if (ok) {
        await reportThisDevice();
        onChanged();
      }
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : "Could not turn on notifications.");
    } finally {
      setBusy(false);
    }
  };

  // tell the messenger what this device is (installed app or tab, permission), as
  // PushResyncListener does once a session; best effort
  const reportThisDevice = async () => {
    let installId: string | null = null;
    try {
      installId = localStorage.getItem(INSTALL_ID_KEY);
    } catch {
      installId = null;
    }
    if (!installId) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = (await reg?.pushManager.getSubscription()) ?? null;
      const body: PushDeviceReport = {
        installId,
        standalone: pwa.isStandalone,
        permission: localPermission() as PushDeviceReport["permission"],
        pushSupported: true,
        endpoint: sub?.endpoint ?? null,
        ...(pwa.isIOS ? { platform: "ios" as const } : {}),
      };
      await reportDevice(body).unwrap();
    } catch {
      /* the boot-time listener reports it next session */
    }
  };

  const install = async () => {
    const choice = await pwa.promptInstall();
    if (choice === "accepted") onChanged();
  };

  const turnOnButton = (label = "Turn on notifications") => (
    <button
      type="button"
      className="gm-button gm-button--block"
      onClick={turnOn}
      disabled={busy || push.subscribing}
    >
      {busy || push.subscribing ? "Turning on..." : label}
    </button>
  );

  const blocked =
    permission === "denied" ? (
      <p>
        {pwa.isStandalone
          ? "Notifications are blocked for n450s. Allow them in your phone's settings for the n450s app, then tap Check again."
          : "Notifications are blocked for n450s.com in this browser. Allow them in the site settings (the icon next to the address), then tap Check again."}
      </p>
    ) : null;

  let body: React.ReactNode;
  if (onHere && pushOn) {
    body = <p>This device gets notifications.</p>;
  } else if (pwa.isStandalone) {
    body =
      blocked ?? (
        <>
          <p>You&apos;re in the app. Turn on notifications and you&apos;re set.</p>
          {turnOnButton()}
        </>
      );
  } else if (pwa.isIOS && !pwa.isSafari) {
    body = (
      <p>
        On iPhone, notifications only work in the app, and the app installs from Safari. Open
        n450s.com in Safari to install it.
      </p>
    );
  } else if (pwa.isIOS) {
    body = (
      <>
        <p>On iPhone, notifications only work in the app. Add it to your home screen:</p>
        <div className="gm-ios-steps">
          <IosInstallSteps />
        </div>
        <p>
          Then open n450s from your home screen, sign in, allow notifications, and come back to
          n450s.com/game to finish joining.
        </p>
      </>
    );
  } else if (pwa.canPrompt) {
    body = (
      <>
        <p>Install the app, then turn on notifications.</p>
        <button type="button" className="gm-button gm-button--lemon gm-button--block" onClick={install}>
          Install the app
        </button>
        {supported && !needsApp ? blocked ?? turnOnButton() : null}
      </>
    );
  } else if (needsApp) {
    body = (
      <p>
        {pwa.isAndroid
          ? "Open the browser menu and tap Install app or Add to Home screen. Then open n450s from your home screen and turn on notifications there."
          : isMacSafari(pwa.isIOS, pwa.isSafari)
            ? "In Safari, choose File, then Add to Dock. Then open n450s from the Dock and turn on notifications there."
            : "Install the app from the install icon in the address bar or the browser menu. Then open it and turn on notifications there."}
      </p>
    );
  } else if (!supported) {
    body = (
      <p>
        This browser can&apos;t show notifications. Use Telegram, or open n450s.com in Chrome or
        Safari.
      </p>
    );
  } else {
    body = (
      <>
        <p>
          {pwa.isAndroid
            ? "Turn on notifications here. To get the app too, open the browser menu and tap Install app or Add to Home screen."
            : "Turn on notifications in this browser."}
        </p>
        {blocked ?? turnOnButton()}
        {isMacSafari(pwa.isIOS, pwa.isSafari) ? (
          <p className="gm-option__note">
            On a Mac, Safari can also add n450s to the Dock: File, then Add to Dock.
          </p>
        ) : null}
      </>
    );
  }

  const error = localError ?? push.error;

  return (
    <section className="gm-option" data-on={pushOn} aria-label="The app with notifications">
      <h3 className="gm-option__head">
        <FontAwesomeIcon icon={faMobileScreenButton} /> The app, with notifications
        {pushOn ? <span className="gm-option__on">On</span> : null}
      </h3>
      {needsApp ? (
        <p>Notifications are on in this browser, but the game only counts them from the app.</p>
      ) : null}
      {body}
      {error ? <p className="gm-error">{error}</p> : null}
    </section>
  );
};

export default PushOption;
